import {
  ErrorHandler,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';
import type { AnalyticsConfig } from './analytics-config';
import { AnalyticsErrorHandler } from './analytics-error-handler.service';
import { POSTHOG_EU_API_HOST } from './analytics.constants';
import { AnalyticsService } from './analytics.service';
import { reportWebVitals } from './report-web-vitals';
import { syncSessionIdentity } from './sync-session-identity';
import { trackRouterEvents } from './track-router-events';

/**
 * Registers PostHog analytics, global error reporting, router page views,
 * Core Web Vitals, and session identity sync.
 *
 * @param config - PostHog key/host and a `disabled` flag for local/demo builds.
 */
export function provideAnalytics(
  config: AnalyticsConfig
): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      if (config.disabled || !config.posthogKey) {
        return;
      }

      const analytics = inject(AnalyticsService);
      analytics.init(config.posthogKey, {
        api_host: config.posthogHost?.trim()
          ? config.posthogHost
          : POSTHOG_EU_API_HOST,
        autocapture: false,
        capture_dead_clicks: false,
        capture_heatmaps: false,
        capture_pageview: false,
        defaults: '2026-01-30',
        disable_session_recording: true,
      });
    }),
    { provide: ErrorHandler, useClass: AnalyticsErrorHandler },
    provideAppInitializer(trackRouterEvents()),
    provideAppInitializer(reportWebVitals()),
    provideAppInitializer(syncSessionIdentity()),
  ]);
}
